import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HierarchyAttributesServiceConstants } from '@app/Constants/HierarchyAttributeServiceConstants';
import { Tenant } from '@app/Models/tenant';
import { LocationAttributeOrder } from '../Models';
import { LocationAttribute } from '../Models/location-attribute.model';
import { tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class LocationAttributesService {
    attributes: LocationAttribute[] = [];
    activeAttributes: LocationAttribute[] = [];
    attributes$: Subject<LocationAttribute[]> = new BehaviorSubject(null);
    listsChanges$: Subject<any[]> = new BehaviorSubject(null);

    private cannotDeleteAttribute$: Subject<any> = new Subject();
    private editAttribute$: Subject<any> = new Subject();
    private deletedAttribute$: Subject<any> = new Subject();
    private drivingAttributes$: Subject<LocationAttribute[]> = new BehaviorSubject(null);
    private apiUrl = '/api/locationattributes/';
    private allowedFieldTypes: string[] = ['text', 'number', 'email', 'datetimepicker', 'checkbox', 'list', 'geolocation', 'calc', 'cron'];

    public constructor(private http: HttpClient) {}

    getHierarchyAttributes(tenant: Tenant): Observable<LocationAttribute[]> {
        return this.http
          .get<LocationAttribute[]>(`${this.apiUrl}${tenant.TenantId}`)
          .pipe(
            tap((response) => (this.attributes = response)),
            catchError(this.handleError)
          );
    }

    getAllowedFieldTypes(): Promise<string[]> {
        return new Promise<string[]>((resolve) => {
            resolve(this.allowedFieldTypes);
        });
    }

    startEditingAttribute(attribute: LocationAttribute): void {
        this.editAttribute$.next({ attribute });
    }

    getEditAttribute(): Observable<{ attribute: LocationAttribute }> {
        return this.editAttribute$.asObservable();
    }

    startAddingAttribute(): void {
        this.editAttribute$.next({ attribute: null});
    }

    getCannotDeleteAttribute$(): Observable<{ attribute: LocationAttribute; reason: string }> {
        return this.cannotDeleteAttribute$.asObservable();
    }

    getDeleteAttribute(): Observable<{ attribute: LocationAttribute }> {
        return this.deletedAttribute$.asObservable();
    }

    deleteAttribute(attribute: LocationAttribute): void {
        this.deletedAttribute$.next({ attribute });
    }

    getDrivingAttributes(): Observable<LocationAttribute[]> {
        return this.drivingAttributes$.asObservable();
    }

    notifyListsUpdate(): void {
        this.listsChanges$.next(this.attributes);
    }

    getListsUpdated(): Observable<any[]> {
        return this.listsChanges$.asObservable();
    }

    addAttribute(attribute: LocationAttribute, tenant: Tenant): Observable<LocationAttribute> {
        return this.http
            .post<LocationAttribute>(`${this.apiUrl}${tenant.TenantId}`, attribute).pipe(map(
                data => {
                    this.attributes.push(data);
                    return data;
                }, (error: Response) => {
                    console.log(error);
                    }
            ));
    }

    updateAttribute(attribute: LocationAttribute, tenant: Tenant): Observable<LocationAttribute> {
        return this.http
            .put<LocationAttribute>(`${this.apiUrl}${tenant.TenantId}`, attribute).pipe(map(
                _ => {
                    this.attributes[attribute.index] = attribute;
                    return attribute;
                }, (error: Response) => {
                    console.log(error);
                    }
            ));
    }

    toggleActiveLocationAttribute(attribute: LocationAttribute, tenant: Tenant): Observable<LocationAttribute> {
        const action: string = !attribute.IsDeactivated ? HierarchyAttributesServiceConstants.deactivateAction :
                                                          HierarchyAttributesServiceConstants.activateAction;
        return this.http
            .post(`${this.apiUrl}${tenant.TenantId}/attribute/${attribute.LocationAttributeId}/${action}`, '').pipe(map(
                data => {
                    this.attributes[attribute.index] = attribute;
                    return attribute;
                }, (error: Response) => {
                    console.log(error);
                    }
            ));
    }

    deleteLocationAttribute(attribute: LocationAttribute, tenant: Tenant): Observable<LocationAttribute> {
        return this.http
            .delete(`${this.apiUrl}${tenant.TenantId}/attribute/${attribute.LocationAttributeId}`).pipe(
                catchError((error: any) => of({ error: error.error })),
                map((data: any) => {
                    if (data && !!data.error) {
                        this.cannotDeleteAttribute$.next({ attribute, reason: data.error[0] });
                    }
                    else {
                        this.attributes[attribute.index] = attribute;
                        this.attributes.splice(this.attributes.indexOf(attribute), 1);
                    }
                    return attribute;
                })
            );
    }

    updateOrder(attributeOrder: LocationAttributeOrder, tenant: Tenant): Observable<any> {
        return this.http
            .post(`${this.apiUrl}${tenant.TenantId}/updateorder`, attributeOrder).pipe(catchError(
                (error: any) => {
                    console.log(error);
                    return of({ error: error.error });
                }
            ));
    }

    private handleError(error: any): Promise<any> {
        console.error('An error occurred', error);
        return Promise.reject(error.message || error);
    }
}
